import React, { useState, useRef, useEffect } from 'react';

interface EditablePositionProps {
  position: number; // Display position (1, 2, 3...)
  maxPosition: number;
  onUpdate: (newPosition: number) => Promise<void>;
  loading?: boolean;
}

export function EditablePosition({ position, maxPosition, onUpdate, loading = false }: EditablePositionProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(position.toString());
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edit value when position prop changes
  useEffect(() => {
    setEditValue(position.toString());
  }, [position]);

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (!loading) {
      setIsEditing(true);
      setError(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers
    if (/^\d*$/.test(value)) {
      setEditValue(value);
      setError(null);
    }
  };

  const handleSave = async () => {
    const newPosition = parseInt(editValue, 10);
    
    // Validate input - only check for positive number
    if (isNaN(newPosition) || newPosition < 1) {
      setError(`Position must be 1 or greater`);
      return;
    }

    // Clamp to maxPosition if greater
    const clampedPosition = Math.min(newPosition, maxPosition);
    
    if (clampedPosition === position) {
      setIsEditing(false);
      return;
    }

    try {
      // Pass the clamped position to the parent
      // The parent will handle mapping this to the correct database position
      await onUpdate(clampedPosition);
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update position');
    }
  };

  const handleCancel = () => {
    setEditValue(position.toString());
    setIsEditing(false);
    setError(null);
  };

  const handleBlur = () => {
    // Only save on blur if the value is valid
    const newPosition = parseInt(editValue, 10);
    if (!isNaN(newPosition) && newPosition >= 1 && newPosition !== position) {
      handleSave();
    } else {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className="flex flex-col">
        <input
          ref={inputRef}
          type="number"
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          min="1"
          max={maxPosition}
          className="w-12 px-1 py-0.5 text-sm font-medium text-gray-500 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          disabled={loading}
        />
        {error && (
          <div className="text-xs text-red-600 mt-1 whitespace-nowrap">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <span 
      className={`text-sm font-medium text-gray-500 ${
        loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:text-gray-700'
      }`}
      onClick={handleClick}
      title={loading ? 'Updating...' : 'Click to edit position'}
    >
      #{position}
    </span>
  );
}
